import { useEffect, useMemo, useState } from "react";
import { searchWallProducts, type WallProductCatalogItem } from "../../api/materials";

export type WallAssemblyLayer = {
  role: string;
  productKey?: string;
  name?: string;
  coverageFactor: number;
  manualWeightPsf?: number;
};

const ROLES = [
  ["exterior_finish", "Exterior finish / cladding"],
  ["wrb_air_barrier", "WRB / air barrier"],
  ["wall_sheathing", "Wall sheathing"],
  ["insulation", "Insulation"],
  ["framing", "Framing / blocking"],
  ["interior_finish", "Interior finish"],
  ["other", "Other"],
] as const;

function number(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function WallAssemblyPicker({
  initialLayers,
  onApply,
  onClose,
}: {
  initialLayers: WallAssemblyLayer[];
  onApply: (layers: WallAssemblyLayer[], previewWeightPsf: number) => void;
  onClose: () => void;
}) {
  const [products, setProducts] = useState<WallProductCatalogItem[]>([]);
  const [layers, setLayers] = useState<WallAssemblyLayer[]>(initialLayers);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    searchWallProducts({ pageSize: 500 })
      .then((page) => {
        if (!alive) return;
        setProducts(page.items);
      })
      .catch((reason) => alive && setError(reason instanceof Error ? reason.message : "Wall product catalog failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const byKey = useMemo(() => new Map(products.map((product) => [product.catalogKey, product])), [products]);
  const categories = useMemo(() => [...new Set(products.map((product) => product.category))].sort(), [products]);
  const manufacturers = useMemo(() => [...new Set(products.map((product) => product.manufacturer))].sort(), [products]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => (
      (!category || product.category === category)
      && (!manufacturer || product.manufacturer === manufacturer)
      && (!term || `${product.name} ${product.manufacturer} ${product.category} ${product.designation ?? ""}`.toLowerCase().includes(term))
    ));
  }, [category, manufacturer, products, search]);

  const total = useMemo(() => layers.reduce((sum, layer) => {
    const productWeight = layer.productKey ? byKey.get(layer.productKey)?.weightPsf : undefined;
    const weight = productWeight ?? number(layer.manualWeightPsf, 0);
    return sum + weight * number(layer.coverageFactor, 1);
  }, 0), [byKey, layers]);

  function patchLayer(index: number, patch: Partial<WallAssemblyLayer>) {
    setLayers((current) => current.map((layer, layerIndex) => layerIndex === index ? { ...layer, ...patch } : layer));
  }

  function addProduct(product: WallProductCatalogItem) {
    setLayers((current) => [...current, {
      role: product.assemblyRole || "other",
      productKey: product.catalogKey,
      coverageFactor: 1,
    }]);
  }

  return (
    <div className="nds-modal-scrim" onClick={onClose}>
      <div className="nds-modal catalog-lookup wall-assembly-lookup" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Wall assembly database">
        <header className="catalog-lookup-head">
          <div>
            <strong>Wall assembly database</strong>
            <span>Build the face-weight ledger that drives this wall-gravity calculation.</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <section className="wall-assembly-summary">
          <header>
            <div><span>Assembly layers</span><strong>{layers.length}</strong></div>
            <div><span>Resolved preview</span><strong>{total.toFixed(3)} psf</strong></div>
            <button type="button" onClick={() => setLayers((current) => [...current, { role: "framing", name: "Manual layer", manualWeightPsf: 0, coverageFactor: 1 }])}>Add manual layer</button>
          </header>
          {layers.length === 0 ? <div className="wall-assembly-empty">No layers selected. Add products from the catalog below or keep the calculation’s manual face weight.</div> : (
            <div className="wall-assembly-layers">
              {layers.map((layer, index) => {
                const product = layer.productKey ? byKey.get(layer.productKey) : undefined;
                const baseWeight = product?.weightPsf ?? number(layer.manualWeightPsf, 0);
                return (
                  <div className={`wall-assembly-layer${product ? "" : " is-manual"}`} key={`${layer.productKey ?? "manual"}-${index}`}>
                    <label><span>Layer role</span><select className="nds-input" value={layer.role} onChange={(event) => patchLayer(index, { role: event.target.value })}>{ROLES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                    <div className="wall-assembly-layer-product">
                      <span>{product ? product.category.replaceAll("_", " ") : "Manual input"}</span>
                      {product ? <strong>{product.name}</strong> : <input className="nds-input" value={layer.name ?? ""} onChange={(event) => patchLayer(index, { name: event.target.value })} />}
                      <small>{product ? `${product.manufacturer} · ${product.weightPsf.toFixed(3)} psf` : "Explicit engineer-supplied layer weight"}</small>
                    </div>
                    {!product ? <label><span>Manual weight</span><input className="nds-input num" type="number" min="0" step="0.001" value={layer.manualWeightPsf ?? 0} onChange={(event) => patchLayer(index, { manualWeightPsf: number(event.target.value, 0) })} /></label> : null}
                    <label><span>Coverage / layers</span><input className="nds-input num" type="number" min="0.01" max="10" step="0.1" value={layer.coverageFactor} onChange={(event) => patchLayer(index, { coverageFactor: number(event.target.value, 1) })} /></label>
                    <div className="wall-assembly-layer-total"><span>Layer total</span><strong>{(baseWeight * number(layer.coverageFactor, 1)).toFixed(3)} psf</strong></div>
                    <button type="button" aria-label={`Remove layer ${index + 1}`} onClick={() => setLayers((current) => current.filter((_, layerIndex) => layerIndex !== index))}>×</button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div className="catalog-lookup-notice">
          Only products with a cataloged lb/ft² value and dead-load eligibility appear. Decorative/acoustic products without a structural weight basis are excluded.
        </div>
        <div className="catalog-lookup-filters wall-product-filters">
          <label className="catalog-search-field"><span>Search products</span><input className="nds-input" placeholder="Board, sheathing, insulation, siding…" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <label><span>Category</span><select className="nds-input" value={category} onChange={(event) => setCategory(event.target.value)}><option value="">All categories</option>{categories.map((value) => <option value={value} key={value}>{value.replaceAll("_", " ")}</option>)}</select></label>
          <label><span>Manufacturer</span><select className="nds-input" value={manufacturer} onChange={(event) => setManufacturer(event.target.value)}><option value="">All manufacturers</option>{manufacturers.map((value) => <option value={value} key={value}>{value}</option>)}</select></label>
        </div>

        <div className="catalog-lookup-results wall-product-results">
          {loading ? <div className="catalog-empty">Loading wall products…</div> : null}
          {!loading && error ? <div className="catalog-empty is-error">{error}</div> : null}
          {!loading && !error && filtered.length === 0 ? <div className="catalog-empty">No wall products match these filters.</div> : null}
          {!loading && !error && filtered.length > 0 ? (
            <table>
              <thead><tr><th>Product</th><th>Layer role</th><th className="num">Weight</th><th /></tr></thead>
              <tbody>{filtered.map((product) => (
                <tr key={product.catalogKey}>
                  <td><strong>{product.name}</strong><span>{[product.brand, product.model, product.designation].filter(Boolean).join(" · ")}</span></td>
                  <td><strong>{product.selectionRole.replaceAll("_", " ")}</strong><span>{product.manufacturer}</span></td>
                  <td className="num"><strong>{product.weightPsf.toFixed(3)} psf</strong><span>catalog face weight</span></td>
                  <td><button type="button" onClick={() => addProduct(product)}>Add layer</button></td>
                </tr>
              ))}</tbody>
            </table>
          ) : null}
        </div>

        {/* Count only — the schema-migration filename is an internal artifact
            and never renders to users. */}
        <footer className="catalog-lookup-foot wall-assembly-foot">
          <span>{filtered.length} eligible products</span>
          <div><button type="button" onClick={onClose}>Cancel</button><button type="button" className="is-primary" disabled={layers.length === 0 || total <= 0} onClick={() => onApply(layers, total)}>Use assembly · {total.toFixed(3)} psf</button></div>
        </footer>
      </div>
    </div>
  );
}
